import React from 'react';
const { useState } = React;
import { textareaSt, labelSt, cardSt, mutedSt, badgeSt, warnSt } from '../diagnostic/shared.jsx';
import { locatorLabel, TRANSFER_FRAGMENTS_MAX, LIFE_KINDS, ownerLabel } from '../../ownerSpaceConfig.js';
import { SOURCE_KINDS, SOURCE_TEXT_LIMITS, METHOD_STATUSES, methodLabel } from '../../methodLibraryConfig.js';
import { SourceProposals, call as libraryCall } from '../MethodLibrary.jsx';
import { ownerCall, Notice } from './shared.jsx';

// "Convertir en herramienta de acompañamiento": from a material of my life
// library to the METHOD library, voluntarily and step by step.
// 1. I pick the fragments (my notes never go by default).
// 2. I see exactly what will be transferred, and can add a text of my own.
// 3. It goes to the method library through the existing flow: the AI proposes
//    tools from that text, I edit them and save them as drafts. Nothing is active
//    until I approve it.
// Where it goes is decided from the real links (target, from the server):
// - no source for this material yet → a new source with the reference to its origin;
// - one source → the chosen fragments it does not have yet are ADDED to it: its
//   fragments and ids (which earlier tools may cite) stay as they are, and the AI
//   proposes only from the new ones;
// - several sources → ambiguous: I see them and pick one myself, or leave it for
//   review. Nothing is chosen, merged or deleted for me.

export default function ToolTransfer({ item, excerpts, content, onClose }) {
  const [step, setStep] = useState('pick');
  const [picked, setPicked] = useState(() => excerpts.slice(0, TRANSFER_FRAGMENTS_MAX).map(x => x.id));
  const [preview, setPreview] = useState(null);
  const [ownText, setOwnText] = useState('');
  const [source, setSource] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [saved, setSaved] = useState([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [chosenTarget, setChosenTarget] = useState('');
  const pieces = [...excerpts, ...content];
  const target = preview?.target || { mode: 'new', candidates: [] };
  const candidate = target.mode === 'append' ? target.candidates[0] : target.candidates.find(c => c.id === chosenTarget) || null;
  const toAdd = candidate ? preview.source.excerpts.filter(x => candidate.toAdd.includes(x.id)) : [];
  const ownExcerpt = () => (ownText.trim() ? [{ id: `own_${Date.now().toString(36)}`, text: ownText.trim(), locator: 'texto propio' }] : []);
  const toggle = (id) => setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : p.length >= TRANSFER_FRAGMENTS_MAX ? p : [...p, id]));

  const showPreview = async () => {
    setBusy('…'); setError('');
    try { setPreview(await ownerCall({ action: 'transferPreview', itemId: item.id, fragmentIds: picked })); setStep('preview'); } catch (e) { setError(e.message); }
    setBusy('');
  };
  // Add to the source this material already has: only the new fragments (and my own text, as one more fragment)
  const append = async () => {
    setError('');
    try {
      setBusy('Agregando los fragmentos nuevos a la fuente existente…');
      const list = [...toAdd, ...ownExcerpt()];
      const r = await libraryCall({ action: 'appendToSource', id: candidate.id, origin: preview.origin, excerpts: list });
      setSource(r.source);
      await ownerCall({ action: 'recordTransfer', itemId: item.id, methodSourceId: r.source.id, mode: 'append', excerpts: r.added.length, notesChars: ownText.trim().length });
      setStep('proposals');
      if (!r.added.length) { setBusy(''); return; }
      setBusy('La IA está proponiendo herramientas solo desde los fragmentos agregados…');
      const d = await libraryCall({ action: 'proposeFromSource', id: r.source.id, excerptIds: r.added });
      setProposals(d.proposals.map((p, i) => ({ ...p, _key: `${Date.now()}-${i}` })));
    } catch (e) { setError(e.message); }
    setBusy('');
  };
  const transfer = async () => {
    setError('');
    try {
      setBusy('Creando la fuente en la biblioteca del método…');
      const payload = { ...preview.source, notes: ownText.trim() };
      const { source: created } = await libraryCall({ action: 'saveSource', source: payload, origin: preview.origin });
      setSource(created);
      await ownerCall({ action: 'recordTransfer', itemId: item.id, methodSourceId: created.id, excerpts: payload.excerpts.length, notesChars: payload.notes.length });
      setStep('proposals');
      setBusy('La IA está proponiendo herramientas desde ese texto…');
      const d = await libraryCall({ action: 'proposeFromSource', id: created.id });
      setProposals(d.proposals.map((p, i) => ({ ...p, _key: `${Date.now()}-${i}` })));
    } catch (e) { setError(e.message); }
    setBusy('');
  };
  const approve = async (entry) => {
    setError('');
    try { const d = await libraryCall({ action: 'approve', id: entry.id, baseVersion: entry.version }); setSaved(list => list.map(x => (x.id === entry.id ? d.entry : x))); }
    catch (e) { setError(e.message); }
  };

  return (
    <div style={{ ...cardSt, borderColor: 'rgba(245,158,11,0.5)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <b>🛠️ Crear herramienta desde este material</b>
        <button className="action-btn" onClick={onClose}>Cerrar</button>
      </div>
      <Notice icon="↗️" tone="transfer">
        Esto pasa de tu <b>biblioteca de vida</b> (privada) a la <b>biblioteca del método</b>, que ven los administradores de Oike que usan la Biblioteca. No llega a clientes ni participantes.
        Nada se activa solo: la IA propone borradores, tú los editas y apruebas. Una herramienta aprobada solo se ofrece en la preparación de sesiones de acompañamiento.
      </Notice>
      {error && <div style={{ ...warnSt, padding: 8 }}>{error}</div>}

      {step === 'pick' && (
        <div>
          <span style={labelSt}>1 · Elige qué fragmentos transferir (hasta {TRANSFER_FRAGMENTS_MAX})</span>
          {!pieces.length && <div style={mutedSt}>Este material no tiene fragmentos ni contenido. Puedes transferir solo sus datos y escribir un texto propio en el paso siguiente.</div>}
          {pieces.map(x => (
            <label key={x.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, margin: '6px 0' }}>
              <input type="checkbox" checked={picked.includes(x.id)} onChange={() => toggle(x.id)} />
              <span><span style={badgeSt(false)}>{x.kind === 'content' ? 'contenido' : 'fragmento'}{locatorLabel(x) ? ` · ${locatorLabel(x)}` : ''}</span> {(x.text || x.preview || '').slice(0, 220)}{(x.chars || 0) > 220 ? '…' : ''}</span>
            </label>
          ))}
          <div style={{ ...mutedSt, margin: '8px 0' }}>Tus notas y reflexiones ({item.notesChars.toLocaleString('es')} caracteres) <b>no se transfieren</b>. Tampoco tus ideas de aplicación.</div>
          <button className="action-btn btn-primary" disabled={!!busy} onClick={showPreview}>Ver exactamente qué se transfiere</button>
        </div>
      )}

      {step === 'preview' && preview && (
        <div>
          <span style={labelSt}>{target.mode === 'new' ? '2 · Esto es exactamente lo que se transfiere' : '2 · Lo que elegiste (más abajo, lo que realmente se agrega a la fuente)'}</span>
          <div style={{ fontSize: 13, display: 'grid', gap: 4, borderLeft: '2px solid rgba(245,158,11,0.6)', paddingLeft: 10 }}>
            <div><b>Título:</b> {preview.source.title}</div>
            {preview.source.creator && <div><b>Autor o creador:</b> {preview.source.creator}</div>}
            <div><b>Tipo:</b> {methodLabel(SOURCE_KINDS, preview.source.kind)} <span style={mutedSt}>(en tu biblioteca: {ownerLabel(LIFE_KINDS, item.kind)})</span></div>
            {preview.source.reference && <div><b>Enlace o referencia:</b> {preview.source.reference}</div>}
            {preview.source.topics.length > 0 && <div><b>Temas:</b> {preview.source.topics.join(', ')}</div>}
            <div><b>Fragmentos ({preview.source.excerpts.length}):</b></div>
            {preview.source.excerpts.map((x, i) => <div key={i} style={{ whiteSpace: 'pre-wrap', fontSize: 12, background: 'rgba(30,42,44,0.03)', padding: 6, borderRadius: 6 }}>{x.locator && <span style={badgeSt(false)}>{x.locator}</span>} {x.text}</div>)}
            <div><b>Origen:</b> referencia a este material de tu biblioteca de vida (solo el id).</div>
          </div>
          <div style={{ marginTop: 10 }}>
            <span style={labelSt}>Texto propio para la biblioteca del método (opcional)</span>
            <textarea style={{ ...textareaSt, minHeight: 80 }} value={ownText} maxLength={SOURCE_TEXT_LIMITS.notes} placeholder="Vacío por defecto. Si quieres sumar algo de tus notas, escríbelo o pégalo aquí: se transfiere tal cual." onChange={e => setOwnText(e.target.value)} />
          </div>
          <div style={{ ...mutedSt, margin: '6px 0' }}>No se transfiere: tus notas ({preview.notIncluded.notesChars.toLocaleString('es')} caracteres), {preview.notIncluded.fragments} fragmentos o partes no elegidos y {preview.notIncluded.applications} ideas de aplicación.</div>
          <Notice icon="✨" tone="ai">Al confirmar, la IA lee solo este texto (los fragmentos y tu texto propio), no el material completo ni el enlace, y propone herramientas separando lo que viene de la fuente, de tus notas y sus sugerencias.</Notice>
          {target.mode !== 'new' && (
            <div style={{ border: '1px solid rgba(245,158,11,0.5)', borderRadius: 8, padding: 10, margin: '8px 0', fontSize: 13 }}>
              {target.mode === 'append' ? (
                <div><b>Este material ya tiene una fuente en la biblioteca del método:</b> «{candidate.title}» ({candidate.excerptCount} fragmento{candidate.excerptCount === 1 ? '' : 's'}). No se crea otra: se le agrega solo lo nuevo, y sus fragmentos actuales (y las herramientas que los citan) no cambian.</div>
              ) : (
                <div>
                  <b>Este material tiene {target.candidates.length} fuentes en la biblioteca del método.</b> Oike no elige, no las fusiona ni borra ninguna: revísalas en Mi biblioteca o elige tú a cuál agregar lo nuevo.
                  {target.candidates.map(c => (
                    <label key={c.id} style={{ display: 'flex', gap: 6, alignItems: 'center', margin: '6px 0' }}>
                      <input type="radio" name="tt-target" checked={chosenTarget === c.id} disabled={c.status !== 'active'} onChange={() => setChosenTarget(c.id)} />
                      «{c.title}» · {c.excerptCount} fragmentos{c.status !== 'active' ? ' · archivada (no se puede agregar)' : ''} · {c.toAdd.length} nuevo{c.toAdd.length === 1 ? '' : 's'}, {c.alreadyPresent.length} ya está{c.alreadyPresent.length === 1 ? '' : 'n'}
                    </label>
                  ))}
                </div>
              )}
              {candidate && <>
                <div style={{ marginTop: 6 }}><b>Se agregará{toAdd.length + ownExcerpt().length === 1 ? '' : 'n'} {toAdd.length + ownExcerpt().length}:</b></div>
                {[...toAdd, ...ownExcerpt()].map(x => <div key={x.id} style={{ whiteSpace: 'pre-wrap', fontSize: 12, background: 'rgba(30,42,44,0.03)', padding: 6, borderRadius: 6, margin: '4px 0' }}>{x.locator && <span style={badgeSt(false)}>{x.locator}</span>} {x.text}</div>)}
                {candidate.alreadyPresent.length > 0 && <div style={mutedSt}>{candidate.alreadyPresent.length} de los fragmentos elegidos ya están en esa fuente: no se repiten.</div>}
                <div style={mutedSt}>La IA propondrá herramientas solo desde lo que se agrega.</div>
              </>}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="action-btn" onClick={() => setStep('pick')}>← Cambiar la selección</button>
            {target.mode === 'new'
              ? <button className="action-btn btn-primary" disabled={!!busy || (!preview.source.excerpts.length && !ownText.trim())} title={!preview.source.excerpts.length && !ownText.trim() ? 'Elige fragmentos o escribe un texto propio: sin texto, la IA no tiene con qué trabajar' : ''} onClick={transfer}>Transferir y pedir propuesta</button>
              : <button className="action-btn btn-primary" disabled={!!busy || !candidate || candidate.status !== 'active' || (!toAdd.length && !ownText.trim())} title={!candidate ? 'Elige a qué fuente agregar' : !toAdd.length && !ownText.trim() ? 'Todo lo elegido ya está en esa fuente' : ''} onClick={append}>Agregar a esa fuente y pedir propuesta</button>}
          </div>
        </div>
      )}

      {step === 'proposals' && (
        <div>
          <span style={labelSt}>3 · Revisa, edita y guarda como borrador</span>
          {source && <div style={mutedSt}>{target.mode === 'new' ? 'Fuente creada' : 'Fragmentos agregados a la fuente'} en la biblioteca del método: "{source.title}". También la encuentras en Mi biblioteca y en Biblioteca → Fuentes.</div>}
          <SourceProposals proposals={proposals} setProposals={setProposals} sources={source ? [source] : []} onSaved={(e) => setSaved(list => [e, ...list])} />
          {!busy && !proposals.length && !saved.length && <div style={mutedSt}>No quedan propuestas.</div>}
          {saved.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <span style={labelSt}>Guardadas</span>
              {saved.map(e => (
                <div key={e.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13, margin: '6px 0' }}>
                  <b>{e.title}</b> <span style={badgeSt(e.status === 'active')}>{methodLabel(METHOD_STATUSES, e.status)}</span>
                  {e.status === 'draft' && <button className="action-btn btn-primary" onClick={() => approve(e)}>Aprobar</button>}
                </div>
              ))}
              <div style={mutedSt}>Aprobar la activa en la biblioteca del método (para preparar sesiones). No se marca como visible para clientes.</div>
            </div>
          )}
        </div>
      )}
      {busy && <div style={{ ...mutedSt, marginTop: 8 }}>{busy}</div>}
    </div>
  );
}
